/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Service, { inject as service } from '@ember/service';
import { post } from 'embercom/lib/ajax';
import type Session from 'embercom/services/session';

export enum ScheduledContentUpdateStatus {
  PENDING = 'pending',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface ScheduledContentUpdate {
  id: number;
  appId: number;
  entityId: number;
  versionId: number;
  entityType: number;
  status: string;
  contentUpdateType: string;
  changeAt: Date;
  adminId: number;
  metadata: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ScheduledContentUpdateWireFormat {
  id: number;
  app_id: number;
  entity_id: number;
  version_id: number;
  entity_type: number;
  status: string;
  content_update_type: string;
  change_at: string;
  admin_id: number;
  metadata: string;
  created_at: string;
  updated_at: string;
}

interface CreateOrUpdateParams {
  entityId: string | number;
  entityType: number;
  versionId: number;
  changeAt: string;
  adminId: number;
  contentUpdateType?: string;
  metadata?: string;
  timezone?: string;
  [key: string]: unknown;
}

interface CancelParams {
  entityId: string | number;
  entityType: number;
}

export default class ScheduledContentUpdateService extends Service {
  @service declare session: Session;

  async createOrUpdate(params: CreateOrUpdateParams): Promise<ScheduledContentUpdate> {
    let response = (await post(
      '/ember/content_service/scheduled_content_updates/create_or_update',
      {
        app_id: this.session.workspace.id,
        entity_id: params.entityId,
        entity_type: params.entityType,
        version_id: params.versionId,
        change_at: params.changeAt,
        admin_id: params.adminId,
        content_update_type: params.contentUpdateType,
        metadata: params.metadata,
        ...(params.timezone ? { timezone: params.timezone } : {}),
      },
    )) as ScheduledContentUpdateWireFormat;

    return this.deserialize(response);
  }

  async cancel(params: CancelParams): Promise<void> {
    await post('/ember/content_service/scheduled_content_updates/cancel', {
      app_id: this.session.workspace.id,
      entity_id: params.entityId,
      entity_type: params.entityType,
    });
  }

  private deserialize(data: ScheduledContentUpdateWireFormat): ScheduledContentUpdate {
    return {
      id: data.id,
      appId: data.app_id,
      entityId: data.entity_id,
      versionId: data.version_id,
      entityType: data.entity_type,
      status: data.status,
      contentUpdateType: data.content_update_type,
      changeAt: new Date(data.change_at),
      adminId: data.admin_id,
      metadata: data.metadata,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
    };
  }
}
